import type { AssistantResponsePayload, AssistantSceneContext } from '../types/assistant';
import type { Locale } from '../../server/src/shared/locale';
import type {
  EventBusMessage,
  EventBusRequest,
  EventBusResponse,
} from '../../server/src/shared/eventBus';
import { $devConfig } from '../state/appConfig';

const DEFAULT_ASSISTANT_API_PATH = '/api/assistant';
const DEFAULT_STREAM_API_PATH = '/api/stream';

const normalizeAssistantApiHost = (value: string | undefined | null): string => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.replace(/\/+$/, '');
};

const getAssistantApiHost = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  const { assistantApiHost } = $devConfig.getState();
  return normalizeAssistantApiHost(assistantApiHost);
};

const getAssistantAuthHeader = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const { assistantApiToken } = $devConfig.getState();
  const token = typeof assistantApiToken === 'string' ? assistantApiToken.trim() : '';
  if (!token) {
    return null;
  }
  return `Bearer ${token}`;
};

const buildAssistantApiUrl = (path: string): string => {
  const host = getAssistantApiHost();
  const suffix = path ? (path.startsWith('/') ? path : `/${path}`) : '';
  return `${host}${DEFAULT_ASSISTANT_API_PATH}${suffix}`;
};

const buildStreamApiUrl = (path: string): string => {
  const host = getAssistantApiHost();
  if (!path) {
    return `${host}${DEFAULT_STREAM_API_PATH}`;
  }
  if (path.startsWith('?') || path.startsWith('#')) {
    return `${host}${DEFAULT_STREAM_API_PATH}${path}`;
  }
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${host}${DEFAULT_STREAM_API_PATH}${suffix}`;
};

const buildAssistantHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const authHeader = getAssistantAuthHeader();
  if (authHeader) {
    headers.Authorization = authHeader;
  }
  return headers;
};

const buildStreamHeaders = (): Record<string, string> => {
  const headers = buildAssistantHeaders();
  delete headers['Content-Type'];
  headers.Accept = 'text/event-stream';
  return headers;
};

type AssistantRequestParams = {
  prompt: string;
  sessionId?: string | null;
  context?: AssistantSceneContext | null;
  locale?: Locale;
};

type SpeechToTextRequestParams = {
  audioBase64: string;
  mimeType: string;
  languageCode?: string;
};

type TextToSpeechRequestParams = {
  text: string;
  voiceName?: string;
  languageCode?: string;
  mimeType?: string;
};

type SpeechToTextResponse = {
  text: string;
  model?: string;
};

type TextToSpeechResponse = {
  audioBase64: string;
  mimeType: string;
  durationSeconds?: number;
};

export async function requestAssistantResponse(
  params: AssistantRequestParams
): Promise<AssistantResponsePayload> {
  const response = await fetch(buildAssistantApiUrl(''), {
    method: 'POST',
    headers: buildAssistantHeaders(),
    body: JSON.stringify({
      prompt: params.prompt,
      sessionId: params.sessionId ?? undefined,
      context: params.context ?? null,
      // locale: params.locale,
    }),
  });

  if (!response.ok) {
    let message = 'Assistant request failed.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  return (await response.json()) as AssistantResponsePayload;
}

export async function publishEventBusEvent(
  params: EventBusRequest
): Promise<EventBusResponse> {
  if (typeof window !== 'undefined') {
    console.info('event-bus: send', params);
  }
  const response = await fetch(buildStreamApiUrl(''), {
    method: 'POST',
    headers: buildAssistantHeaders(),
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    let message = 'Event bus request failed.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  return (await response.json()) as EventBusResponse;
}

type EventBusStreamHandlers = {
  onMessage: (message: EventBusMessage) => void;
  onSession?: (sessionId: string) => void;
  onError?: (error: Error) => void;
};

const parseEventStream = async (
  response: Response,
  handlers: EventBusStreamHandlers,
  signal?: AbortSignal
): Promise<void> => {
  if (!response.body) {
    throw new Error('Event stream body is empty.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let dataLines: string[] = [];

  const dispatch = () => {
    if (dataLines.length === 0) {
      return;
    }
    const data = dataLines.join('\n');
    dataLines = [];
    try {
      const parsed = JSON.parse(data) as Partial<EventBusMessage> & {
        sessionId?: string;
      };
      if (parsed && parsed.event) {
        if (typeof window !== 'undefined') {
          console.info('event-bus: receive', parsed);
        }
        handlers.onMessage(parsed as EventBusMessage);
        return;
      }
      if (parsed?.sessionId && handlers.onSession) {
        if (typeof window !== 'undefined') {
          console.info('event-bus: session', parsed.sessionId);
        }
        handlers.onSession(parsed.sessionId);
      }
    } catch (error) {
      if (handlers.onError) {
        handlers.onError(
          error instanceof Error ? error : new Error('Failed to parse SSE payload.')
        );
      }
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    if (signal?.aborted) {
      try {
        reader.cancel();
      } catch {
        // ignore
      }
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    let lineBreakIndex = buffer.indexOf('\n');
    while (lineBreakIndex !== -1) {
      const line = buffer.slice(0, lineBreakIndex).replace(/\r$/, '');
      buffer = buffer.slice(lineBreakIndex + 1);

      if (!line) {
        dispatch();
        lineBreakIndex = buffer.indexOf('\n');
        continue;
      }

      if (line.startsWith(':')) {
        lineBreakIndex = buffer.indexOf('\n');
        continue;
      }

      const [field, ...rest] = line.split(':');
      const valuePart = rest.join(':').trimStart();

      if (field === 'event') {
        void valuePart;
      } else if (field === 'data') {
        dataLines.push(valuePart);
      }

      lineBreakIndex = buffer.indexOf('\n');
    }
  }
};

export async function subscribeEventBus(
  sessionId: string,
  handlers: EventBusStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const response = await fetch(
    buildStreamApiUrl(`?sessionId=${encodeURIComponent(sessionId)}`),
    {
      method: 'GET',
      headers: buildStreamHeaders(),
      signal,
    }
  );

  if (!response.ok) {
    let message = 'Failed to connect to event stream.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  await parseEventStream(response, handlers, signal);
}

const blobToBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read audio data.'));
        return;
      }
      const base64 = result.split(',')[1] ?? '';
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read audio data.'));
    reader.readAsDataURL(blob);
  });

export async function requestSpeechToText(
  blob: Blob,
  params: Omit<SpeechToTextRequestParams, 'audioBase64'>
): Promise<SpeechToTextResponse> {
  const audioBase64 = await blobToBase64(blob);
  const response = await fetch(buildAssistantApiUrl('speech-to-text'), {
    method: 'POST',
    headers: buildAssistantHeaders(),
    body: JSON.stringify({
      audio: audioBase64,
      mimeType: params.mimeType,
      languageCode: params.languageCode,
    }),
  });

  if (!response.ok) {
    let message = 'Failed to transcribe audio.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  return (await response.json()) as SpeechToTextResponse;
}

export async function requestTextToSpeech(
  params: TextToSpeechRequestParams,
  signal?: AbortSignal
): Promise<TextToSpeechResponse> {
  const response = await fetch(buildAssistantApiUrl('text-to-speech'), {
    method: 'POST',
    headers: buildAssistantHeaders(),
    signal,
    body: JSON.stringify({
      text: params.text,
      voiceName: params.voiceName,
      languageCode: params.languageCode,
      mimeType: params.mimeType,
    }),
  });

  if (!response.ok) {
    let message = 'Failed to synthesize speech.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  return (await response.json()) as TextToSpeechResponse;
}

export async function requestTextToSpeechStream(
  params: TextToSpeechRequestParams,
  signal?: AbortSignal
): Promise<Response> {
  const response = await fetch(buildAssistantApiUrl('text-to-speech/stream'), {
    method: 'POST',
    headers: buildAssistantHeaders(),
    signal,
    body: JSON.stringify({
      text: params.text,
      voiceName: params.voiceName,
      languageCode: params.languageCode,
      mimeType: params.mimeType,
    }),
  });

  if (!response.ok) {
    let message = 'Failed to synthesize speech.';
    try {
      const errorBody = (await response.json()) as { message?: string };
      if (typeof errorBody?.message === 'string') {
        message = errorBody.message;
      }
    } catch {
      // Ignore JSON parsing errors – fall back to generic text
    }
    throw new Error(message);
  }

  return response;
}
